import React, { useEffect, useState } from 'react';
import { dietaryProtocols, familyDietaryProfile } from '../../api/client';

// Kitchen-wide dietary protocol selector. Lives on the admin page.
// Reads/writes the FamilyDietaryProfile via /api/family_dietary_profile.
// Per-user profile editing (hard avoids, preferences, etc.) is not built yet.
export default function DietaryProtocolTab() {
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocolId, setSelectedProtocolId] = useState('');
  const [activeProtocol, setActiveProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([dietaryProtocols.list(), familyDietaryProfile.get()])
      .then(([protocolsResp, profileResp]) => {
        if (cancelled) return;
        setProtocols(protocolsResp.data.dietary_protocols || []);
        const profile = profileResp.data.family_dietary_profile || {};
        setSelectedProtocolId(profile.dietary_protocol_id ? String(profile.dietary_protocol_id) : '');
        setActiveProtocol(profile.dietary_protocol || null);
      })
      .catch((err) => {
        if (!cancelled) setErrorMessage(err.response?.data?.errors?.join(', ') || err.message || 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSave = () => {
    setSaving(true);
    setStatusMessage(null);
    setErrorMessage(null);
    familyDietaryProfile
      .update({ dietary_protocol_id: selectedProtocolId === '' ? null : selectedProtocolId })
      .then((resp) => {
        const profile = resp.data.family_dietary_profile || {};
        setActiveProtocol(profile.dietary_protocol || null);
        setStatusMessage('Saved.');
      })
      .catch((err) => {
        setErrorMessage(err.response?.data?.errors?.join(', ') || err.message || 'Failed to save');
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-gray-500 dark:text-gray-400">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Kitchen Dietary Protocol</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Pick one named eating protocol that applies to this kitchen. The AI assistant uses it to ground
          meal-planning and recipe suggestions. Per-member detail (hard avoids, preferences) isn&apos;t in
          the UI yet — it&apos;s set via the chat assistant.
        </p>
      </div>

      <div>
        <label htmlFor="dietary-protocol-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Protocol
        </label>
        <select
          id="dietary-protocol-select"
          className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          value={selectedProtocolId}
          onChange={(e) => setSelectedProtocolId(e.target.value)}
        >
          <option value="">— No protocol selected —</option>
          {protocols.map((p) => (
            <option key={p.id} value={p.id}>
              {p.display_name}{p.scope === 'user' ? ' (private)' : ''}
            </option>
          ))}
        </select>
      </div>

      {activeProtocol && activeProtocol.short_description && (
        <div className="text-sm text-gray-600 dark:text-gray-300 border-l-4 border-indigo-200 dark:border-indigo-700 pl-3">
          <strong>{activeProtocol.display_name}:</strong> {activeProtocol.short_description}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {statusMessage && <span className="text-sm text-green-600 dark:text-green-400">{statusMessage}</span>}
        {errorMessage && <span className="text-sm text-red-600 dark:text-red-400">{errorMessage}</span>}
      </div>
    </div>
  );
}
